import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { theme } from '../theme/theme'
import { Womb } from './icons/Womb'
import { Close } from './icons/Close'
import { Caret } from './icons/Caret'
import { Stethoscope } from './icons/Stethoscope'
import { ChatPlus } from './icons/ChatPlus'
import { Title4, BodyMedium } from './styles/Typography'
import { FeatureFlagId } from '../api/ester-b2b-api/private/featureFlags/types'
import { useIsFeatureEnabled } from '../state/selectors'

export const AskUsWidget = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const isEnabled = useIsFeatureEnabled(FeatureFlagId.SHOW_MESSAGES_POPUP)

  if (!isEnabled) return null

  return (
    <Container>
      {open && (
        <Panel aria-label={t('employee.yourHealth.askUsHeader')}>
          <PanelHeader>
            <Womb size={28} color={theme.color.plum} />
            <PanelHeaderTitle>{t('employee.yourHealth.askUsHeader')}</PanelHeaderTitle>
            <CloseButton type="button" onClick={() => setOpen(false)} aria-label={t('common.form.close')}>
              <Close size={24} color={theme.color.plum} />
            </CloseButton>
          </PanelHeader>
          <PanelBody>
            <IconCircle>
              <Stethoscope size={24} color={theme.color.plum} />
            </IconCircle>
            <PanelTitle>{t('employee.yourHealth.askUsTitle')}</PanelTitle>
            <PanelDescription>{t('employee.yourHealth.askUsDescription')}</PanelDescription>
            <StartConversationLink to="/portal/messages/new" state={{ from: '/portal/your-health/health-plan' }}>
              <ChatPlus size={20} color={theme.color.white} />
              {t('employee.yourHealth.askUsCta')}
            </StartConversationLink>
          </PanelBody>
        </Panel>
      )}
      {open ? (
        <CloseFab type="button" onClick={() => setOpen(false)} aria-label={t('common.form.close')}>
          <DownChevron>
            <Caret size={28} color={theme.color.white} />
          </DownChevron>
        </CloseFab>
      ) : (
        <PillButton type="button" onClick={() => setOpen(true)} aria-label={t('employee.yourHealth.askUs')}>
          <PillLabel>{t('employee.yourHealth.askUs')}</PillLabel>
          <Womb size={24} color={theme.color.white} />
        </PillButton>
      )}
    </Container>
  )
}

const Container = styled.div`
  position: fixed;
  right: ${theme.spacing.mediumLarge}px;
  bottom: ${theme.spacing.mediumLarge}px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.medium}px;
  z-index: 1000;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    right: ${theme.spacing.medium}px;
    bottom: ${theme.spacing.medium}px;
    left: ${theme.spacing.medium}px;
  }
`

const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding: ${theme.spacing.small}px ${theme.spacing.mediumLarge}px;
  background: ${theme.color.plumMid};
  border: none;
  border-radius: 999px;
  color: ${theme.color.white};
  cursor: pointer;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
    0px 6px 14px 0px rgba(0, 0, 0, 0.15);

  &:hover {
    background: ${theme.color.plum};
  }
`

const PillLabel = styled.span`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.medium}px;
  font-weight: 500;
  line-height: 24px;
`

const Panel = styled.div`
  width: 340px;
  background: ${theme.color.plumLight};
  border-radius: 16px;
  box-shadow:
    0px 4px 12px rgba(0, 0, 0, 0.08),
    0px 12px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    width: 100%;
  }
`

const PanelHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  padding: ${theme.spacing.medium}px ${theme.spacing.mediumLarge}px;
`

const PanelHeaderTitle = styled.h3`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.medium}px;
  font-weight: 500;
  color: ${theme.color.plum};
  text-align: center;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing.mediumLarge}px ${theme.spacing.mediumLarge}px ${theme.spacing.large}px;
  text-align: center;
  gap: ${theme.spacing.medium}px;
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${theme.color.beigeDark};
`

const PanelTitle = styled(Title4)`
  margin: 0;
  color: ${theme.color.plum};
`

const PanelDescription = styled(BodyMedium)`
  margin: 0;
  color: ${theme.color.plum};
`

const StartConversationLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  padding: ${theme.spacing.small}px ${theme.spacing.mediumLarge}px;
  background: ${theme.color.plum};
  border-radius: 999px;
  color: ${theme.color.white};
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.default}px;
  font-weight: 500;
  line-height: 24px;
  text-decoration: none;
  margin-top: ${theme.spacing.xsmall}px;

  &:hover {
    background: ${theme.color.orange};
  }
`

const CloseFab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${theme.color.plumMid};
  border: none;
  color: ${theme.color.white};
  cursor: pointer;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
    0px 6px 14px 0px rgba(0, 0, 0, 0.15);

  &:hover {
    background: ${theme.color.plum};
  }
`

const DownChevron = styled.div`
  display: flex;
  transform: rotate(90deg);
`
